import React, { useEffect, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { markersToDoc, docToMarkers } from "@/lib/richDoc";
import { RichTextToolbar } from "@/components/composer/RichTextToolbar";
import { Textarea } from "@/components/ui/textarea";
import { useGuard } from "@/lib/guards";

// Lot 778 — champ de publication avec aperçu en direct : gras, italique, souligné, puces et citation s'affichent tels
// quels pendant la frappe (Tiptap). `value` / `onChange` restent le texte à repères (**gras**…) : brouillons, limites,
// mentions, hashtags et le serveur ne changent pas. `editorRef.current` = l'élément éditable (focus / scrollIntoView,
// comme l'ancien <textarea>) ; un changement de `value` venu de l'extérieur (emoji, « @ », mention choisie) est
// appliqué avec le curseur en fin de texte.
const TiptapEditor = ({ value, onChange, placeholder, className = "", style, tone = "light", autoFocus = false, editorRef, toolbar = true, toolbarClassName = "", testId = "composer-textarea", toolbarTestId = "composer-rich-toolbar" }) => {
  const last = useRef(value || "");
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, code: false, strike: false, link: false, orderedList: false, horizontalRule: false, dropcursor: false, gapcursor: false, trailingNode: false }),
      Placeholder.configure({ placeholder: placeholder || "" }),
    ],
    content: markersToDoc(value || ""),
    autofocus: autoFocus ? "end" : false,
    onUpdate: ({ editor: ed }) => { const s = ed.isEmpty ? "" : docToMarkers(ed.getJSON()); last.current = s; onChange?.(s); },
    editorProps: { attributes: { class: `post-rte outline-none ${className}`, "data-testid": testId, "data-tone": tone, role: "textbox", "aria-multiline": "true" } },
  });

  useEffect(() => {
    if (!editor) return;
    editor.view.dom.yalolaEditor = editor;   // tests / outils : accès à l'instance depuis l'élément
    if (editorRef) editorRef.current = editor.view.dom;
    if (style) Object.assign(editor.view.dom.style, style);
  }, [editor, editorRef, style]);

  // Valeur changée hors de l'éditeur (emoji, « @ » / « # », mention ou hashtag choisi, brouillon) → contenu + curseur en fin.
  useEffect(() => {
    if (!editor) return;
    const v = value || "";
    if (v === last.current) return;
    last.current = v;
    editor.commands.setContent(markersToDoc(v), { emitUpdate: false });
    editor.commands.focus("end");
  }, [editor, value]);

  useEffect(() => { if (editor && placeholder !== undefined) editor.extensionManager.extensions.find((e) => e.name === "placeholder").options.placeholder = placeholder; }, [editor, placeholder]);

  return (
    <div data-testid={`${testId}-wrap`}>
      {toolbar && <RichTextToolbar editor={editor} tone={tone} className={toolbarClassName} testId={toolbarTestId} />}
      <EditorContent editor={editor} />
    </div>
  );
};

// Garde-fou coupé (lot 780) → champ d'avant le lot 778 : <textarea> + barre d'outils qui insère les repères visibles.
const PlainEditor = ({ value, onChange, placeholder, className = "", style, tone = "light", autoFocus = false, editorRef, toolbar = true, toolbarClassName = "", testId = "composer-textarea", toolbarTestId = "composer-rich-toolbar" }) => {
  const ownRef = useRef(null);
  const ref = editorRef || ownRef;
  return (
  <div data-testid={`${testId}-wrap`} data-plain="1">
    {toolbar && <RichTextToolbar textareaRef={ref} value={value} onChange={onChange} tone={tone} className={toolbarClassName} testId={toolbarTestId} />}
    <Textarea
      ref={ref}
      autoFocus={autoFocus}
      placeholder={placeholder}
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      className={`${tone === "dark" ? "bg-transparent border-0 p-0 text-white placeholder:text-white/60 focus-visible:ring-0 shadow-none resize-none" : "resize-none"} ${className}`}
      style={style}
      rows={3}
      data-testid={testId}
      data-tone={tone}
    />
  </div>
  );
};

export const PostRichEditor = (props) => (useGuard("composer_rich_preview") ? <TiptapEditor {...props} /> : <PlainEditor {...props} />);

export default PostRichEditor;
